//UserMgmt.vue
<template>
  <v-card flat>
    <v-card-title class="d-flex align-center py-3">
      <span class="text-h6">User Management</span>
      <v-spacer></v-spacer>
      <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          hide-details
          outlined
          dense
          class="mx-4"
          style="max-width: 300px"
      ></v-text-field>
      <v-btn-toggle v-model="showOnlyScm" mandatory dense>
        <v-btn small :value="false">All</v-btn>
        <v-btn small :value="true">SCM</v-btn>
      </v-btn-toggle>
    </v-card-title>

    <v-card-text>
      <v-data-table
          :headers="headers"
          :items="filteredItems"
          :items-per-page="15"
          :loading="loading"
          dense
          @click:row="showUserDetails"
      >
        <!-- SCM 상태 -->
        <template v-slot:item.scm="{ item }">
          <v-icon small :color="item.linked ? 'success' : 'grey'">
            {{ item.linked ? 'mdi-link-variant' : 'mdi-link-variant-off' }}
          </v-icon>
        </template>

        <!-- Admin -->
        <template v-slot:item.isAdmin="{ item }">
          <v-icon
              v-if="item.admin"
              small
              color="amber darken-2"
          >
            mdi-crown
          </v-icon>
          <v-icon v-else
            small
            color='grey'
            >
            mdi-crown
          </v-icon>
        </template>

        <!-- SCM Role -->
        <template v-slot:item.role="{ item }">
          <v-chip v-if="item.linked" x-small :color="roleColor(scmRole(item))" dark>{{ scmRole(item) }}</v-chip>
          <span v-else class="grey--text caption">—</span>
        </template>

        <!-- Deploy stacks -->
        <template v-slot:item.stacks="{ item }">
          <span v-if="!item.linked || scmRole(item) === 'admin'" class="grey--text caption">{{ scmRole(item) === 'admin' ? 'all' : '—' }}</span>
          <span v-else-if="!item.scopes || !item.scopes.length" class="grey--text caption">none</span>
          <template v-else>
            <v-chip v-for="st in item.scopes" :key="st" x-small class="mr-1 mb-1" outlined>{{ st }}</v-chip>
          </template>
        </template>

        <!-- Username -->
        <template v-slot:item.username="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" class="mr-2">
              <v-img :src="item.avatarUrl || '/default-avatar.png'"></v-img>
            </v-avatar>
            {{ item.username }}
          </div>
        </template>

        <!-- 상태 -->
        <template v-slot:item.state="{ item }">
          <v-chip x-small :color="getStateColor(item.state)" dark>
            {{ item.state }}
          </v-chip>
        </template>

        <template v-slot:item.lastLoginAt="{ item }">
          <div class="d-flex flex-column">
            <span>{{ formatDate(item.lastLoginAt) }}</span>
            <span class="caption grey--text">
        {{ getTimeAgo(item.lastLoginAt) }}
      </span>
          </div>
        </template>

        <!-- 액션 -->
        <template v-slot:item.actions="{ item }">
          <v-btn icon x-small class="mr-1" @click="showUserDetails(item)">
            <v-icon small color="primary">mdi-information</v-icon>
          </v-btn>
          <v-btn
              v-if="item.linked"
              icon
              x-small
              @click.stop="toggleUserStatus(item)"
              :color="item.enabled ? 'error' : 'success'"
          >
            <v-icon small>
              {{ item.enabled ? 'mdi-account-off' : 'mdi-account-check' }}
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- 사용자 상세 정보 다이얼로그 -->
    <v-dialog v-model="detailDialog" max-width="800">
      <user-info-card
          v-if="selectedUser"
          :user="selectedUser"
          :available-stacks="availableStacks"
          @toggle-status="toggleUserStatus"
          @update-role="onUpdateRole"
          @update-scopes="onUpdateScopes"
          @close="detailDialog = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import userApi from '@/config/api/users'
import swarmApi from '@/config/api/swarm'
import UserInfoCard from './UserInfoCard.vue'

export default {
  components: {
    UserInfoCard
  },

  data: () => ({
    headers: [
      { text: 'SCM', value: 'scm', width: 48, align: 'center' },
      { text: 'Admin', value: 'isAdmin', width: 48, align: 'center' },
      { text: 'SCM Role', value: 'role', width: 90, align: 'center', sortable: false },
      { text: 'Stacks', value: 'stacks', sortable: false },
      { text: 'Username', value: 'username', width: 200 },
      { text: 'Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'State', value: 'state', width: 100 },
      { text: 'Last Login', value: 'lastLoginAt', width: 150 },
      { text: '', value: 'actions', align: 'center', sortable: false, width: 90 }
    ],
    items: [],
    search: '',
    showOnlyScm: false,
    loading: false,
    detailDialog: false,
    selectedUser: null,
    availableStacks: []
  }),

  computed: {
    filteredItems() {
      return this.items
          .filter(item => {
            if (this.showOnlyScm) return item.linked
            return true
          })
          .filter(item => {
            if (!this.search) return true
            const searchLower = this.search.toLowerCase()
            return (
                item.username?.toLowerCase().includes(searchLower) ||
                item.name?.toLowerCase().includes(searchLower) ||
                item.email?.toLowerCase().includes(searchLower)
            )
          })
    }
  },

  methods: {
    getStateColor(state) {
      return {
        active: 'success',
        blocked: 'error',
        inactive: 'grey'
      }[state] || 'grey'
    },

    showUserDetails(user) {
      this.selectedUser = user;
      this.detailDialog = true;
    },

    async refreshUsers() {
      this.loading = true
      try {
        this.items = await userApi.list()
      } catch (error) {
        console.error('Failed to load users:', error)
        this.$notify.error('Error', 'Failed to load user list')
      }
      this.loading = false
    },

    scmRole(item) {
      const r = item.roles || []
      if (r.includes('ADMIN')) return 'admin'
      if (r.includes('USER')) return 'user'
      return 'guest'
    },

    roleColor(role) {
      return { admin: 'deep-purple', user: 'blue', guest: 'blue-grey' }[role] || 'grey'
    },

    async onUpdateRole({ user, roles }) {
      try {
        await userApi.updateRole({ userId: user.scmId, roles })
        this.$notify.success('Success', `${user.username} role updated`)
        this.detailDialog = false
        this.refreshUsers()
      } catch (error) {
        this.$notify.error('Error', 'Failed to update role')
      }
    },

    async onUpdateScopes({ user, scopes }) {
      try {
        await userApi.updateScopes({ userId: user.scmId, scopes })
        this.$notify.success('Success', `${user.username} deploy stacks updated`)
        this.detailDialog = false
        this.refreshUsers()
      } catch (error) {
        this.$notify.error('Error', 'Failed to update deploy stacks')
      }
    },

    async fetchStacks() {
      try {
        const services = await swarmApi.listServices({ clstNm: 'demo' })
        const set = new Set()
        ;(services || []).forEach(s => {
          const ns = s.Spec && s.Spec.Labels && s.Spec.Labels['com.docker.stack.namespace']
          if (ns) set.add(ns)
        })
        this.availableStacks = [...set].sort()
      } catch (e) {
        console.error('Failed to load stacks:', e)
      }
    },

    async toggleUserStatus(user) {
      try {
        const action = user.enabled ? 'disable' : 'enable'
        await userApi.updateStatus({
          userId: user.scmId,
          action: action
        })
        this.$notify.success('Success', `User ${action === 'enable' ? 'Activate' : 'Deactivate'} Completed`)
        this.refreshUsers()
      } catch (error) {
        this.$notify.error('Error', 'Failed to change user status')
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).replace(/\//g, '.').replace(',', '')
    },

    getTimeAgo(date) {
      if (!date) return ''
      const diff = new Date() - new Date(date)
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      if (days > 0) return `${days}일 전`
      const hours = Math.floor(diff / (1000 * 60 * 60))
      if (hours > 0) return `${hours}시간 전`
      const minutes = Math.floor(diff / (1000 * 60))
      return `${minutes}분 전`
    }
  },

  mounted() {
    this.refreshUsers()
    this.fetchStacks()
  }
}
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table ::v-deep td {
  padding: 0 8px !important;
}
.v-data-table ::v-deep tbody tr {
  cursor: pointer;
}
</style>